import {BaseCommand} from './base.command';
import {ElementSnapshot, GraphNodeSnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {firstValueFrom} from 'rxjs';
import {determineObjectType, UndoLabelMetadata} from '../undo-label.utils';

/**
 * DeleteCommand handles deletion of nodes and links with full undo support.
 * Uses soft-delete on the backend and restoreWithChildren for undo.
 */
export class DeleteCommand extends BaseCommand<ElementSnapshot> {
  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    snapshot: ElementSnapshot
  ) {
    const nodeCount = snapshot.nodes.length;
    const linkCount = snapshot.links.length;
    const totalCount = nodeCount + linkCount;

    // Determine object type for label
    const objectType = determineObjectType(nodeCount, linkCount);
    const labelMetadata: UndoLabelMetadata = {
      object: objectType,
      verb: 'Deletion',
      elementCount: objectType === 'Selection' ? totalCount : undefined
    };

    super('delete', 'Deletion', {before: snapshot, after: {nodes: [], links: []}}, labelMetadata);
  }

  async execute(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {nodes, links} = this.snapshot.before;

    // Remove from Cytoscape first for immediate feedback
    cy.batch(() => {
      // Remove links first to avoid issues with connected nodes
      links.forEach(link => {
        const element = cy.$(`#${link.id}`);
        if (element.length > 0) {
          element.remove();
        }
      });

      // Remove nodes (children will be removed automatically by Cytoscape)
      nodes.forEach(node => {
        const element = cy.$(`#${node.id}`);
        if (element.length > 0) {
          element.remove();
        }
      });
    });

    // Soft-delete on backend
    const deletePromises: Promise<unknown>[] = [];

    // Delete nodes
    for (const node of nodes) {
      deletePromises.push(firstValueFrom(this.nodeService.deleteNode(node.id)));
    }

    // Delete links
    for (const link of links) {
      deletePromises.push(firstValueFrom(this.nodeService.deleteLink(link.id)));
    }

    await Promise.all(deletePromises);
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {nodes, links} = this.snapshot.before;

    // Collect all IDs for restoration
    const allIds = [
      ...nodes.map(n => n.id),
      ...links.map(l => l.id)
    ];

    // Restore via backend API (handles soft-deleted records)
    await firstValueFrom(this.nodeService.restoreWithChildren(allIds));

    // Re-add to Cytoscape with proper ordering
    cy.batch(() => {
      // Add nodes first, ordered by hierarchy (parents before children)
      const sortedNodes = this.sortNodesByHierarchy(nodes);

      for (const node of sortedNodes) {
        // Check if node already exists (shouldn't, but be safe)
        if (cy.$(`#${node.id}`).length > 0) continue;

        cy.add({
          group: 'nodes',
          data: {...node.data} as any,
          position: {...node.position},
          classes: node.classes.join(' ')
        });

        // If node has a parent, move it to the parent
        if (node.parentId) {
          const addedNode = cy.$(`#${node.id}`);
          if (addedNode.length > 0) {
            addedNode.move({parent: node.parentId});
          }
        }
      }

      // Then add links
      for (const link of links) {
        // Check if link already exists
        if (cy.$(`#${link.id}`).length > 0) continue;

        cy.add({
          group: 'edges',
          data: {
            ...link.data,
            source: link.sourceId,
            target: link.targetId
          }
        });
      }
    });

    // Reinitialize edge editing handles for restored links with control/bend points
    this.reinitializeEdgeHandles(cy, links);

    // Trigger UI refresh
    cy.style().update();
  }

  /**
   * Reinitialize edge editing handles for restored links
   */
  private reinitializeEdgeHandles(cy: any, links: any[]): void {
    if (!(cy as any).edgeEditing) return;
    const edgeEditingInstance = (cy as any).edgeEditing('get');
    if (!edgeEditingInstance) return;

    for (const link of links) {
      const edge = cy.$(`#${link.id}`);
      if (edge.length === 0) continue;

      const linkType = link.data.type || 'STRAIGHT';
      const controlPoints = link.data.controlPointPositions;
      const bendPoints = link.data.bendPointPositions;

      // Update classes based on link type
      edge.removeClass('edgebendediting-hasbendpoints');
      edge.removeClass('edgecontrolediting-hascontrolpoints');

      if (linkType === 'BEZIER') {
        edge.addClass('ignore_control_points');
      } else {
        edge.removeClass('ignore_control_points');
      }

      // Add appropriate class if we have anchor data
      if (linkType === 'UNBUNDLED_BEZIER' && controlPoints?.length > 0) {
        edge.addClass('edgecontrolediting-hascontrolpoints');
      } else if (linkType === 'STRAIGHT' && bendPoints?.length > 0) {
        edge.addClass('edgebendediting-hasbendpoints');
      }

      // Reinitialize anchor points visualization
      const edgeEditingType = linkType === 'BEZIER' ? 'none' : linkType === 'STRAIGHT' ? 'bend' : 'control';
      edgeEditingInstance.initAnchorPoints([edge], edgeEditingType);
    }
  }

  /**
   * Sort nodes so parents come before children.
   * This ensures proper restoration order.
   */
  private sortNodesByHierarchy(nodes: GraphNodeSnapshot[]): GraphNodeSnapshot[] {
    const nodeMap = new Map(nodes.map(n => [n.id, n]));
    const sorted: GraphNodeSnapshot[] = [];
    const visited = new Set<string>();

    const visit = (node: GraphNodeSnapshot) => {
      if (visited.has(node.id)) return;

      // Visit parent first if it's in our list
      if (node.parentId && nodeMap.has(node.parentId)) {
        visit(nodeMap.get(node.parentId)!);
      }

      visited.add(node.id);
      sorted.push(node);
    };

    nodes.forEach(node => visit(node));
    return sorted;
  }
}
